import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Button, Layout, Text, ToastOutlet} from '@phonectas/unifon-ui-kit';
import {useTranslation} from 'react-i18next';
import {Routes, Route, Navigate, useLocation} from 'react-router-dom';

import 'common/i18n';
// import '../../../assets/favicon.png';
import AppBar from './components/AppBar';
import MainNav from './components/MainNav';
import {ROUTES} from 'common/constants';
import {useUserRoles} from 'common/hooks/useUserRoles';
import ProtectedRoute from 'common/components/ProtectedRoute';
import Login from 'containers/Auth';
import Logout from '../Auth/logout';
import BMODashboard from '../BMO';
import useUserStore from 'common/stores/user';
import AuthService from 'services/auth';
import 'utils/date';
import Profile from '../Profile';
import ThreeSixtyDashboard from '../ThreeSixty';
import Unauthorized from '../Unauthorized';
import RedirectFromBn from '../ThreeSixty/RedirectFromBn';
import useThreeSixtyNewStore from 'common/stores/threeSixtyStore';
import {shallow} from 'zustand/shallow';
import ThreeSixtyCustomer from 'containers/ThreeSixty/components';
import SnakkChat from 'containers/ThreeSixty/components/SnakkChat';
import useBmoStore from 'common/stores/bmoStore';
import UnifonThemeProvider from '@phonectas/unifon-ui-kit/dist/theme/provider';
import GlobalStyleWrapper from 'common/GlobalStyleWrapper';
import MrcDashboard from 'containers/MRC';
import useMrcStore from 'common/stores/mrcStore';
import PermissionManager from 'common/components/permissions/PermissionManager';
import {findRootRoute, getRouteSection} from 'common/components/permissions/constants';
import ProfileModel from 'common/models/profileModel';

/**
 * @module RootModule
 * @return {JSX.Element}
 * @constructor
 */
const App: React.FC = () => {
	const [t] = useTranslation();
	const user = useUserStore((state) => state.user);
	const userLoadFailed = useUserStore((state) => state.userLoadFailed);
	const getUser = useUserStore((state) => state.getUser);
	const setActiveUser = useBmoStore((state) => state.setActiveUser);
	const setCatalogId = useBmoStore((state) => state.setCatalogId);
	const location = useLocation();
	const {
		setCurrentUser,
		getUserConfig,
		userConfig,
	} = useThreeSixtyNewStore((state) => ({
		setCurrentUser: state.setCurrentUser,
		getUserConfig: state.getUserConfig,
		userConfig: state.userConfig,
	}), shallow);
	const fetchMrcTemplates = useMrcStore((state) => state.fetchMrcTemplates);
	const {canUseUserConfig, isCustomerService} = useUserRoles();
	const [expandMainNav, setExpandMainNav] = useState(false);
	const toggleNav = useCallback(() => setExpandMainNav((prev) => !prev), []);
	const retryGetUser = useCallback(() => {
		getUser();
	}, [getUser]);

	const permissionManager = useMemo(
		() => user ? new PermissionManager(user) : null,
		[user]
	);
	
	// Get user's saved default route preference
	const userConfigDefaultRoute = useMemo(() => {
		if (!userConfig) return undefined;
		const profileModel = ProfileModel.fromApiResponse(userConfig);
		return profileModel.get('default_route') || undefined;
	}, [userConfig]);

	// Get default route based on user roles and preferences
	const defaultRoute = useMemo(() => {
		if (!permissionManager) return ROUTES.UNAUTHORIZED_USER;
		return permissionManager.getDefaultRoute(userConfigDefaultRoute);
	}, [permissionManager, userConfigDefaultRoute]);

	useEffect(() => {
		if (AuthService.isValidToken()) {
			getUser();
		}
	}, [getUser]);

	useEffect(() => {
		if (user) {
			setActiveUser(user?.name);
			setCurrentUser(user?.name);
			setCatalogId(user?.catalogID);
		}
	}, [user, setActiveUser, setCurrentUser, setCatalogId]);

	useEffect(() => {
		if (!userConfig && canUseUserConfig && AuthService.isValidToken()) {
			getUserConfig();
		}
	}, [getUserConfig, userConfig, canUseUserConfig]);

	// Only the MRC pages use the templates, and the backend only allows the customer service role
	useEffect(() => {
		if (isCustomerService) {
			fetchMrcTemplates();
		}
	}, [fetchMrcTemplates, isCustomerService]);

	if (!user) {
		if (!userLoadFailed) return null;
		return (
			<UnifonThemeProvider theme="unifon">
				<Layout fullWidth fullHeight direction="column" alignItems="center" justifyContent="center" gap="md">
					<Text variant="h3">{t('Could not load your user. Check your connection and try again.')}</Text>
					<Button variant="primary" onClick={retryGetUser}>{t('Try again')}</Button>
				</Layout>
				<GlobalStyleWrapper />
			</UnifonThemeProvider>
		);
	}

	// Check if the current route is protected and user lacks permission
	if (permissionManager) {
		const protectedRoute = findRootRoute(location.pathname);
		if (protectedRoute && !permissionManager.hasAccess(protectedRoute)) {
			// Pass requiredRoles as state to Unauthorized
			return (
				<Navigate
					to="/unauthorized"
					state={{requiredRoles: permissionManager.getRequiredRoles(protectedRoute)}}
					replace
				/>
			);
		}
	}

	return (
		<UnifonThemeProvider theme="unifon">
			<AppBar onToggleMenu={toggleNav} />
			<Layout
				gap="none"
				nowrap
				fullWidth
				fullHeight
				alignItems="stretch"
				autoOverflow
				style={{minWidth: 1000}}
			>
				<MainNav expand={expandMainNav} />
				<Routes>
					<Route path={ROUTES.BMO_ROUTE} element={<ProtectedRoute><BMODashboard /></ProtectedRoute>} />
					<Route path={ROUTES.PROFILE} element={<ProtectedRoute><Profile /></ProtectedRoute>} />
					<Route path={ROUTES.THREE_SIXTY} element={
						<ProtectedRoute><ThreeSixtyDashboard /></ProtectedRoute>
					} />
					<Route path={ROUTES.THREE_SIXTY_CUSTOMER} element={
						<ProtectedRoute><ThreeSixtyCustomer /></ProtectedRoute>
					} />
					<Route path={ROUTES.THREE_SIXTY_NUMBER_NUMB} element={
						<ProtectedRoute><RedirectFromBn /></ProtectedRoute>
					} />
					<Route path={ROUTES.MRC} element={<ProtectedRoute><MrcDashboard /></ProtectedRoute>} />
					<Route path={ROUTES.UNAUTHORIZED_USER} element={<Unauthorized />} />
					<Route path="login" element={<Login />} />
					<Route path="logout" element={<Logout />} />
					<Route path="*" element={<Navigate to={defaultRoute} />} />
				</Routes>
			</Layout>
			<ToastOutlet />
			<SnakkChat active={getRouteSection(location.pathname) === getRouteSection(ROUTES.THREE_SIXTY)} />
			<GlobalStyleWrapper />
		</UnifonThemeProvider>
	);
}

export default App;
